import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';

import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { useSession } from '../../stores/session';
import type { UndiscoveredArtist } from './api';
import { Verified } from '../../components/ui/Verified';

/**
 * "Artists nobody's found yet."
 *
 * ## The threshold is the feature
 *
 * Under 500 followers, not already followed, and with at least one released
 * track. A suggestion rail that ranked by popularity would recommend the same
 * six people to everybody and make the problem it exists to solve worse - the
 * artists who need finding are precisely the ones no ranking surfaces.
 *
 * Each card carries one line of evidence, computed server-side from counts that
 * request actually read. Where there is nothing true to say, the line is absent
 * rather than filled with something warm and unfalsifiable.
 */
export function ArtistRail({ artists }: { artists: UndiscoveredArtist[] }) {
  if (artists.length === 0) return null;

  return (
    <section aria-labelledby="undiscovered-heading">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="undiscovered-heading" className="text-2xl font-semibold tracking-tight text-ink">
          Artists nobody's found yet
        </h2>
        {/*
          The rule, stated. The mockup says "updated hourly", which would be
          untrue here - the query runs on every visit and the follower counts
          behind it move the moment somebody presses follow.
        */}
        <span className="meta-caps text-faint">under 500 followers · not yet followed by you</span>
      </div>

      <ul className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-artist-rail>
        {artists.map((artist) => (
          <li key={artist.id}>
            <ArtistCard artist={artist} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function ArtistCard({ artist }: { artist: UndiscoveredArtist }) {
  const me = useSession((state) => state.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const follow = useMutation({
    mutationFn: () => api.post(`/users/${artist.username}/follow`),
    // The rail excludes people you follow, so a successful follow means this
    // card is about to disappear. Refetching the page is what removes it.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
  });

  return (
    <Card className="flex h-full flex-col gap-4 p-5" data-artist={artist.username}>
      <Link to={`/${artist.username}`} className="group flex flex-col gap-3">
        <Avatar src={artist.avatar} name={artist.display_name} size={52} />

        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate font-medium text-ink group-hover:underline">
            {artist.display_name}
            {artist?.verified && <Verified />}
          </span>
          <span className="truncate font-mono text-xs text-faint">@{artist.username}</span>
        </div>
      </Link>

      {artist.signal ? (
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-fresh" aria-hidden />
          <span className="truncate font-mono text-xs text-fresh" title={artist.signal}>
            {artist.signal}
          </span>
        </div>
      ) : (
        <span className="font-mono text-xs text-faint">
          {artist.track_count === 1 ? '1 track' : `${artist.track_count} tracks`}
        </span>
      )}

      {/* mt-auto so the buttons line up across cards whose signal lines wrap to
          different heights. A ragged row of buttons reads as a broken grid. */}
      {/* Offered signed out, and routed to sign-in rather than hidden: this
          rail is the best argument on the page for having an account, so
          removing its only action from the people who need convincing is
          exactly backwards. */}
      <Button
        className="mt-auto w-full"
        loading={follow.isPending}
        disabled={follow.isSuccess}
        onClick={() => (me ? follow.mutate() : navigate('/login'))}
        data-follow-artist
      >
        {follow.isSuccess ? 'Following' : 'Follow'}
      </Button>
    </Card>
  );
}
